const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATE_TIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/;

const pad = (value) => String(value).padStart(2, "0");

const fromParts = (year, month, day) => {
	if (!year || !month || !day) return null;
	return { day, month, year };
};

const shiftUtcDate = (year, month, day, deltaDays) => {
	const utc = new Date(Date.UTC(year, month - 1, day + deltaDays));
	return {
		day: utc.getUTCDate(),
		month: utc.getUTCMonth() + 1,
		year: utc.getUTCFullYear(),
	};
};

const partsFromUtcMidnightDate = (date) =>
	fromParts(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());

export const getCalendarDateParts = (value) => {
	if (value == null || value === "") return null;

	if (typeof value === "string") {
		const trimmed = value.trim();
		const dateOnly = trimmed.match(DATE_ONLY_PATTERN);
		if (dateOnly) {
			return fromParts(Number(dateOnly[1]), Number(dateOnly[2]), Number(dateOnly[3]));
		}

		const dateTime = trimmed.match(DATE_TIME_PATTERN);
		if (dateTime) {
			const year = Number(dateTime[1]);
			const month = Number(dateTime[2]);
			const day = Number(dateTime[3]);
			const hours = Number(dateTime[4]);
			const minutes = Number(dateTime[5]);
			const seconds = Number(dateTime[6] || 0);
			const hasZone = /Z$/i.test(trimmed) || /[+-]\d{2}:?\d{2}$/.test(trimmed);

			if (hours === 0 && minutes === 0 && seconds === 0) {
				return fromParts(year, month, day);
			}

			if (hasZone && minutes === 0 && seconds === 0 && hours >= 12) {
				return shiftUtcDate(year, month, day, 1);
			}

			if (hasZone) {
				const parsed = new Date(trimmed);
				if (!Number.isNaN(parsed.getTime())) {
					if (parsed.getUTCHours() === 0 && parsed.getUTCMinutes() === 0) {
						return partsFromUtcMidnightDate(parsed);
					}
					if (parsed.getUTCMinutes() === 0 && parsed.getUTCHours() >= 12) {
						return shiftUtcDate(
							parsed.getUTCFullYear(),
							parsed.getUTCMonth() + 1,
							parsed.getUTCDate(),
							1,
						);
					}
					return partsFromUtcMidnightDate(parsed);
				}
			}

			return fromParts(year, month, day);
		}
	}

	const date = value instanceof Date ? value : new Date(value);
	if (Number.isNaN(date.getTime())) return null;

	if (date.getUTCHours() === 0 && date.getUTCMinutes() === 0 && date.getUTCSeconds() === 0) {
		return partsFromUtcMidnightDate(date);
	}

	if (date.getUTCMinutes() === 0 && date.getUTCSeconds() === 0 && date.getUTCHours() >= 12) {
		return shiftUtcDate(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate(), 1);
	}

	return partsFromUtcMidnightDate(date);
};

export const toDateOnlyISO = (value) => {
	const parts = getCalendarDateParts(value);
	if (!parts) return null;
	return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
};

export const formatDateUS = (value) => {
	const parts = getCalendarDateParts(value);
	if (!parts) return value ? String(value) : "—";
	return `${parts.month}/${parts.day}/${parts.year}`;
};

export const formatVisitDate = (value) => {
	const parts = getCalendarDateParts(value);
	if (!parts) return "—";
	return new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).toLocaleDateString("en-US", {
		day: "numeric",
		month: "short",
		timeZone: "UTC",
		year: "numeric",
	});
};

export const formatTimeUS = (value) => {
	if (!value) return "—";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "—";
	return date.toLocaleTimeString("en-US", {
		hour: "numeric",
		hour12: true,
		minute: "2-digit",
	});
};
